/* eslint-disable no-restricted-imports */
import { useCallback } from 'react';

import { ApolloClient } from '@apollo/client';
import { useApiClient } from 'api/client';
import {
  LegalClientBeneficiaryInfoDocument,
  LegalClientHeadInfoDocument,
  LegalClientInfoDocument,
  LegalClientLegalHeadFirstHeadInfoDocument,
  LegalClientRepresentativeInfoDocument,
  PersonClientInfoDocument,
  PersonClientRepresentativeInfoDocument,
  Query_ClientController_GetById_OnboardingStatus as ClientStatusTypeApi,
} from 'api/generated/graphql';
import { LegalClientStatusTypeApi } from 'api/requests/onboarding/legal/helpers/parseLegalClientOnboardingStatus';
import { ClientIdentityType } from 'api/types/data';
import { ClientInfoEntity, OnboardingProcessEntity } from 'api/types/entity';
import { isAccountDisabled } from 'config';

interface ClientInfoApi {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  onboardingStatus?: ClientStatusTypeApi | LegalClientStatusTypeApi;
  onboarding?: {
    process?: OnboardingProcessEntity;
  };
  personalAccount?: {
    number?: string;
  };
}

const parseClientInfo = async (
  client: ApolloClient<object>,
  type: ClientIdentityType,
  clientInfo?: ClientInfoApi | null
): Promise<ClientInfoEntity | undefined> => {
  if (!clientInfo) return undefined;

  const parse = (clientInfo: ClientInfoApi) => ({
    id: clientInfo.id,
    firstName: clientInfo.firstName,
    lastName: clientInfo.lastName,
    middleName: clientInfo.middleName,
    isStartOnboarding:
      !!clientInfo.onboarding?.process?.previousSteps.length ||
      (clientInfo.onboardingStatus &&
        clientInfo.onboardingStatus !== ClientStatusTypeApi.FillingOut),
    type,
    isAccountOpened:
      !isAccountDisabled() &&
      clientInfo.onboardingStatus === ClientStatusTypeApi.AccountOpened,

    personalAccountNumber: clientInfo.personalAccount?.number,
  });

  const parseLegalClientRepresentative = async (
    clientInfo: ClientInfoApi & { legalClientId?: string }
  ) => {
    let companyName: string | undefined;
    let personalAccountNumber: string | undefined | null;
    let onboardingStatus: LegalClientStatusTypeApi | undefined;

    if (clientInfo.legalClientId) {
      const { data } = await client.query<{ clientInfo: ClientInfoApi }>({
        query: LegalClientInfoDocument,
        variables: { id: clientInfo.legalClientId },
      });
      if (data.clientInfo.onboardingStatus) {
        onboardingStatus = data.clientInfo
          .onboardingStatus as LegalClientStatusTypeApi;
      }
      personalAccountNumber = data.clientInfo.personalAccount?.number;

      companyName = data.clientInfo.fullName;
    }

    return {
      ...parse({ ...clientInfo, onboardingStatus }),
      ownerId: clientInfo.legalClientId,
      companyName,
      personalAccountNumber,
    };
  };

  const parseClientRepresentative = async (
    clientInfo: ClientInfoApi & { clientId?: string }
  ) => {
    let onboardingStatus: ClientStatusTypeApi | undefined;

    if (clientInfo.clientId) {
      const { data } = await client.query<{ clientInfo: ClientInfoApi }>({
        query: PersonClientInfoDocument,
        variables: { id: clientInfo.clientId },
      });
      if (data.clientInfo.onboardingStatus) {
        onboardingStatus = data.clientInfo
          .onboardingStatus as ClientStatusTypeApi;
      }
    }

    return {
      ...parse({ ...clientInfo, onboardingStatus }),
      isAccountOpened: false,
      ownerId: clientInfo.clientId,
    };
  };

  switch (type) {
    case ClientIdentityType.Client:
    case ClientIdentityType.ClientWithRepresentative:
    case ClientIdentityType.LegalClientHead:
    case ClientIdentityType.LegalClientLegalHeadFirstHead:
    case ClientIdentityType.LegalClientBeneficiary: {
      return parse(clientInfo as ClientInfoApi);
    }

    case ClientIdentityType.ClientRepresentative: {
      return parseClientRepresentative(clientInfo as ClientInfoApi);
    }

    case ClientIdentityType.LegalClientRepresentative: {
      return parseLegalClientRepresentative(clientInfo as ClientInfoApi);
    }

    default:
      return undefined;
  }
};

export const useClientInfo = () => {
  const client = useApiClient();

  const getClientInfo = useCallback(
    async (id: string, type: ClientIdentityType) => {
      let doc;
      switch (type) {
        case ClientIdentityType.Client:
        case ClientIdentityType.ClientWithRepresentative:
          doc = PersonClientInfoDocument;
          break;

        case ClientIdentityType.ClientRepresentative:
          doc = PersonClientRepresentativeInfoDocument;
          break;

        case ClientIdentityType.LegalClientRepresentative:
          doc = LegalClientRepresentativeInfoDocument;
          break;

        case ClientIdentityType.LegalClientHead:
          doc = LegalClientHeadInfoDocument;
          break;

        case ClientIdentityType.LegalClientLegalHeadFirstHead:
          doc = LegalClientLegalHeadFirstHeadInfoDocument;
          break;

        case ClientIdentityType.LegalClientBeneficiary:
          doc = LegalClientBeneficiaryInfoDocument;
          break;

        default:
          return undefined;
      }

      const { data } = await client.query({
        query: doc,
        variables: { id },
      });

      return parseClientInfo(client, type, data.clientInfo);
    },
    [client]
  );

  return { getClientInfo };
};
